"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ProjectModelValidationErrorType = void 0;
exports.validateProjectModel = validateProjectModel;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
var ProjectModelValidationErrorType;
(function (ProjectModelValidationErrorType) {
    ProjectModelValidationErrorType["DuplicateProjectModuleName"] = "duplicate-project-module-name";
    ProjectModelValidationErrorType["InvalidComponentReference"] = "invalid-component-reference";
    ProjectModelValidationErrorType["InvalidFootprintReference"] = "invalid-footprint-reference";
    ProjectModelValidationErrorType["InvalidInstanceDesignId"] = "invalid-instance-design-id";
    ProjectModelValidationErrorType["InvalidInstanceModuleRef"] = "invalid-instance-module-ref";
    ProjectModelValidationErrorType["DuplicateSchematicModuleRef"] = "duplicate-schematic-module-ref";
    ProjectModelValidationErrorType["InvalidSchematicComponentReference"] = "invalid-schematic-component-reference";
    ProjectModelValidationErrorType["InvalidSchematicFootprintReference"] = "invalid-schematic-footprint-reference";
    ProjectModelValidationErrorType["InvalidPinReference"] = "invalid-pin-reference";
    ProjectModelValidationErrorType["InvalidProjectModuleLinkage"] = "invalid-project-module-linkage";
    ProjectModelValidationErrorType["NetNameMismatch"] = "net-name-mismatch";
    ProjectModelValidationErrorType["InvalidNetModuleReference"] = "invalid-net-module-reference";
    ProjectModelValidationErrorType["InvalidNetPinReference"] = "invalid-net-pin-reference";
    ProjectModelValidationErrorType["InvalidConnectionModuleName"] = "invalid-connection-module-name";
    ProjectModelValidationErrorType["DuplicateNetConnection"] = "duplicate-net-connection";
    ProjectModelValidationErrorType["EmptyNet"] = "empty-net";
    ProjectModelValidationErrorType["SinglePinNet"] = "single-pin-net";
    ProjectModelValidationErrorType["DrawingOnlyNet"] = "drawing-only-net";
    ProjectModelValidationErrorType["OrphanedPinConnection"] = "orphaned-pin-connection";
    ProjectModelValidationErrorType["InvalidPinNetReference"] = "invalid-pin-net-reference";
    ProjectModelValidationErrorType["MissingFootprints"] = "missing-footprints";
    ProjectModelValidationErrorType["MissingSymbols"] = "missing-symbols";
    ProjectModelValidationErrorType["PinCountMismatch"] = "pin-count-mismatch";
    ProjectModelValidationErrorType["PadCountMismatch"] = "pad-count-mismatch";
    ProjectModelValidationErrorType["InvalidSignalPinReference"] = "invalid-signal-pin-reference";
    ProjectModelValidationErrorType["InvalidSignalPadReference"] = "invalid-signal-pad-reference";
    ProjectModelValidationErrorType["DuplicatePinInSignals"] = "duplicate-pin-in-signals";
    ProjectModelValidationErrorType["DuplicateInstanceName"] = "duplicate-instance-name";
    ProjectModelValidationErrorType["InvalidInstanceSheetIndex"] = "invalid-instance-sheet-index";
    ProjectModelValidationErrorType["MultiInstanceMismatch"] = "multi-instance-mismatch";
    ProjectModelValidationErrorType["UnusedComponent"] = "unused-component";
    ProjectModelValidationErrorType["UnusedFootprint"] = "unused-footprint";
    ProjectModelValidationErrorType["MissingMetadata"] = "missing-metadata";
    ProjectModelValidationErrorType["InconsistentNetConnection"] = "InconsistentNetConnection";
})(ProjectModelValidationErrorType || (exports.ProjectModelValidationErrorType = ProjectModelValidationErrorType = {}));
const DEFAULT_OPTIONS = {
    emptyNetSeverity: undefined,
    singlePinNetSeverity: undefined,
    drawingOnlyNetSeverity: undefined,
    unusedComponentSeverity: sigma_engine_shared_1.BoundaryLevel.Warning,
    unusedFootprintSeverity: undefined,
    missingFootprintsSeverity: sigma_engine_shared_1.BoundaryLevel.Warning,
    missingSymbolsSeverity: sigma_engine_shared_1.BoundaryLevel.Warning,
    multiInstanceMismatchSeverity: sigma_engine_shared_1.BoundaryLevel.Error,
    strictPinValidation: true,
};
function validateProjectModel(projectModel, options = {}) {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    const innerResults = [];
    innerResults.push(validateProjectModules(projectModel, opts));
    for (const [designId, design] of Object.entries(projectModel.designs)) {
        if (design.modelType === 'schematic') {
            innerResults.push(validateSchematic(projectModel, design, designId, opts));
        }
    }
    innerResults.push(validateComponents(projectModel, opts));
    innerResults.push(validateStructuralIssues(projectModel, opts));
    const result = {
        type: 'project-model-validation',
        message: 'Project model validation complete',
        innerResults,
    };
    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(result);
    return result;
}
function validateProjectModules(projectModel, _opts) {
    const innerResults = [];
    const moduleNames = new Set();
    const duplicateNames = new Set();
    for (const module of projectModel.modules) {
        if (moduleNames.has(module.name)) {
            duplicateNames.add(module.name);
        }
        moduleNames.add(module.name);
    }
    if (duplicateNames.size > 0) {
        innerResults.push({
            type: ProjectModelValidationErrorType.DuplicateProjectModuleName,
            message: `Duplicate project module names found: ${Array.from(duplicateNames).join(', ')}`,
            severity: sigma_engine_shared_1.BoundaryLevel.Error,
            duplicateNames: Array.from(duplicateNames),
        });
    }
    for (const module of projectModel.modules) {
        if (!projectModel.components[module.componentRef]) {
            innerResults.push({
                type: ProjectModelValidationErrorType.InvalidComponentReference,
                message: `Project module "${module.name}" references non-existent component "${module.componentRef}"`,
                severity: sigma_engine_shared_1.BoundaryLevel.Error,
                moduleName: module.name,
                componentRef: module.componentRef,
            });
        }
    }
    for (const module of projectModel.modules) {
        for (const instance of module.instances) {
            if (!projectModel.designs[instance.designId]) {
                innerResults.push({
                    type: ProjectModelValidationErrorType.InvalidInstanceDesignId,
                    message: `Project module "${module.name}" instance references non-existent design "${instance.designId}"`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
                    moduleName: module.name,
                    designId: instance.designId,
                });
                continue;
            }
            const design = projectModel.designs[instance.designId];
            if (design.modelType === 'schematic') {
                const moduleExists = design.modules.some((m) => m.ref === instance.ref);
                if (!moduleExists) {
                    innerResults.push({
                        type: ProjectModelValidationErrorType.InvalidInstanceModuleRef,
                        message: `Project module "${module.name}" instance references non-existent module ref "${instance.ref}" in design "${instance.designId}"`,
                        severity: sigma_engine_shared_1.BoundaryLevel.Error,
                        moduleName: module.name,
                        designId: instance.designId,
                        moduleRef: instance.ref,
                    });
                }
            }
        }
    }
    const result = {
        type: 'project-module-validation',
        message: innerResults.length === 0 ? 'All project modules are valid' : 'Project module validation found issues',
        innerResults,
    };
    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(result);
    return result;
}
function validateSchematic(projectModel, schematic, designId, opts) {
    const innerResults = [];
    innerResults.push(validateSchematicModules(projectModel, schematic, designId, opts));
    innerResults.push(validateNets(projectModel, schematic, designId, opts));
    if (schematic.multiInstanceConfiguration) {
        innerResults.push(validateMultiInstanceConfiguration(schematic, designId));
    }
    const result = {
        type: 'schematic-validation',
        message: `Schematic "${designId}" validation`,
        innerResults,
        designId,
    };
    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(result);
    return result;
}
function validateSchematicModules(projectModel, schematic, designId, opts) {
    const innerResults = [];
    const moduleRefs = new Set();
    const duplicateRefs = new Set();
    for (const module of schematic.modules) {
        if (moduleRefs.has(module.ref)) {
            duplicateRefs.add(module.ref);
        }
        moduleRefs.add(module.ref);
    }
    if (duplicateRefs.size > 0) {
        innerResults.push({
            type: ProjectModelValidationErrorType.DuplicateSchematicModuleRef,
            message: `Duplicate module REFs in schematic "${designId}": ${Array.from(duplicateRefs).join(', ')}`,
            severity: sigma_engine_shared_1.BoundaryLevel.Error,
            designId,
            duplicateRefs: Array.from(duplicateRefs),
        });
    }
    for (const module of schematic.modules) {
        if (!projectModel.components[module.componentRef]) {
            innerResults.push({
                type: ProjectModelValidationErrorType.InvalidSchematicComponentReference,
                message: `Module "${module.ref}" in schematic "${designId}" references non-existent component "${module.componentRef}"`,
                severity: sigma_engine_shared_1.BoundaryLevel.Error,
                designId,
                moduleRef: module.ref,
                componentRef: module.componentRef,
            });
            continue;
        }
        const component = projectModel.components[module.componentRef];
        const isSchematic = schematic.modelType === 'schematic';
        const collectionName = isSchematic ? 'symbol' : 'footprint';
        const footprintExists = isSchematic ?
            component.symbols.some((fp) => fp.ref === module.footprintRef)
            : component.footprints.some((fp) => fp.ref === module.footprintRef);
        if (!footprintExists) {
            innerResults.push({
                type: ProjectModelValidationErrorType.InvalidSchematicFootprintReference,
                message: `Module "${module.ref}" in ${schematic.modelType} "${designId}" references non-existent ${collectionName} "${module.footprintRef}" in component "${module.componentRef}"`,
                severity: sigma_engine_shared_1.BoundaryLevel.Error,
                designId,
                moduleRef: module.ref,
                componentRef: module.componentRef,
                footprintRef: module.footprintRef,
            });
        }
        if (opts.strictPinValidation) {
            const allPins = new Set();
            for (const symbol of component.symbols) {
                for (const pin of symbol.pins) {
                    allPins.add(pin.ref);
                }
            }
            for (const pinRef of Object.keys(module.pinNets)) {
                if (!allPins.has(pinRef)) {
                    innerResults.push({
                        type: ProjectModelValidationErrorType.InvalidPinReference,
                        message: `Module "${module.ref}" in schematic "${designId}" references non-existent pin "${pinRef}" in component "${module.componentRef}"`,
                        severity: sigma_engine_shared_1.BoundaryLevel.Error,
                        designId,
                        moduleRef: module.ref,
                        componentRef: module.componentRef,
                        pinRef,
                    });
                }
            }
        }
        if (!(component.isVirtual && module.componentRef.startsWith('schref_'))) {
            const lookupName = module.fullModuleName ?? module.name;
            const projectModule = projectModel.modules.find((pm) => pm.name === lookupName);
            if (!projectModule) {
                innerResults.push({
                    type: ProjectModelValidationErrorType.InvalidProjectModuleLinkage,
                    message: `Module "${module.ref}" in schematic "${designId}" does not link to any project module (lookup name: "${lookupName}")`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
                    designId,
                    moduleRef: module.ref,
                    moduleName: module.name,
                    fullModuleName: module.fullModuleName,
                    lookupName,
                });
            }
        }
        if (opts.multiInstanceMismatchSeverity) {
            const hasSchematicMultiInstance = !!schematic.multiInstanceConfiguration;
            const hasModuleMultiInstance = !!module.multiInstance;
            if (hasSchematicMultiInstance !== hasModuleMultiInstance) {
                innerResults.push({
                    type: ProjectModelValidationErrorType.MultiInstanceMismatch,
                    message: `Module "${module.ref}" in schematic "${designId}" has multi-instance mismatch: schematic has ${hasSchematicMultiInstance ? 'multi-instance' : 'no multi-instance'} but module has ${hasModuleMultiInstance ? 'multi-instance' : 'no multi-instance'}`,
                    severity: opts.multiInstanceMismatchSeverity,
                    designId,
                    moduleRef: module.ref,
                    hasSchematicMultiInstance,
                    hasModuleMultiInstance,
                });
            }
        }
    }
    const result = {
        type: 'schematic-module-validation',
        message: innerResults.length === 0 ?
            `All modules in schematic "${designId}" are valid`
            : `Module validation found issues in schematic "${designId}"`,
        innerResults,
        designId,
    };
    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(result);
    return result;
}
function validateNets(projectModel, schematic, designId, opts) {
    const innerResults = [];
    const moduleMap = new Map();
    for (const module of schematic.modules) {
        moduleMap.set(module.ref, module);
    }
    const componentPinMap = new Map();
    for (const module of schematic.modules) {
        if (!componentPinMap.has(module.componentRef)) {
            const component = projectModel.components[module.componentRef];
            if (component) {
                const pins = new Set();
                for (const symbol of component.symbols) {
                    for (const pin of symbol.pins) {
                        pins.add(pin.ref);
                    }
                }
                componentPinMap.set(module.componentRef, pins);
            }
        }
    }
    for (const [netName, net] of Object.entries(schematic.nets)) {
        if (net.name !== netName) {
            innerResults.push({
                type: ProjectModelValidationErrorType.NetNameMismatch,
                message: `Net key "${netName}" does not match net name "${net.name}" in schematic "${designId}"`,
                severity: sigma_engine_shared_1.BoundaryLevel.Error,
                designId,
                netKey: netName,
                netName: net.name,
            });
        }
        if (opts.emptyNetSeverity && net.connections.length === 0) {
            innerResults.push({
                type: ProjectModelValidationErrorType.EmptyNet,
                message: `Net "${netName}" in schematic "${designId}" has no connections`,
                severity: opts.emptyNetSeverity,
                designId,
                netName,
            });
        }
        if (opts.drawingOnlyNetSeverity && net.connections.length > 0) {
            const reachesRealPart = net.connections.some((connection) => {
                const module = moduleMap.get(connection.moduleRef);
                return module !== undefined && projectModel.components[module.componentRef]?.isVirtual !== true;
            });
            if (!reachesRealPart) {
                innerResults.push({
                    type: ProjectModelValidationErrorType.DrawingOnlyNet,
                    message: `Net "${netName}" in schematic "${designId}" connects only to drawing-only placements ` +
                        `(${net.connections.map((c) => c.moduleName).join(', ')}), so it names no resolvable module`,
                    severity: opts.drawingOnlyNetSeverity,
                    designId,
                    netName,
                });
            }
        }
        if (opts.singlePinNetSeverity && net.connections.length === 1) {
            innerResults.push({
                type: ProjectModelValidationErrorType.SinglePinNet,
                message: `Net "${netName}" in schematic "${designId}" has only one connection`,
                severity: opts.singlePinNetSeverity,
                designId,
                netName,
                connection: net.connections[0],
            });
        }
        const connectionSet = new Set();
        for (const connection of net.connections) {
            const module = moduleMap.get(connection.moduleRef);
            if (!module) {
                innerResults.push({
                    type: ProjectModelValidationErrorType.InvalidNetModuleReference,
                    message: `Net "${netName}" in schematic "${designId}" references non-existent module "${connection.moduleRef}"`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
                    designId,
                    netName,
                    moduleRef: connection.moduleRef,
                });
                continue;
            }
            if (connection.moduleName !== (module.fullModuleName ?? module.name)) {
                innerResults.push({
                    type: ProjectModelValidationErrorType.InvalidConnectionModuleName,
                    message: `Net "${netName}" in schematic "${designId}" has connection with moduleName "${connection.moduleName}" but module "${connection.moduleRef}" has name "${module.name}"`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                    designId,
                    netName,
                    moduleRef: connection.moduleRef,
                    expectedModuleName: module.name,
                    actualModuleName: connection.moduleName,
                });
            }
            const componentPins = componentPinMap.get(module.componentRef);
            if (componentPins && !componentPins.has(connection.pinRef)) {
                innerResults.push({
                    type: ProjectModelValidationErrorType.InvalidNetPinReference,
                    message: `Net "${netName}" in schematic "${designId}" references non-existent pin "${connection.pinRef}" on module "${connection.moduleRef}" (component "${module.componentRef}")`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
                    designId,
                    netName,
                    moduleRef: connection.moduleRef,
                    pinRef: connection.pinRef,
                    componentRef: module.componentRef,
                });
            }
            const pinNetValue = module.pinNets[connection.pinRef];
            if (pinNetValue?.valueOf() !== netName?.valueOf()) {
                innerResults.push({
                    type: ProjectModelValidationErrorType.InconsistentNetConnection,
                    message: `Net "${netName}" in schematic "${designId}" is connected to module "${connection.moduleRef}" pin "${connection.pinRef}" but pinNets references net "${pinNetValue}"`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
                    designId,
                    netName,
                    moduleRef: connection.moduleRef,
                    pinRef: connection.pinRef,
                    expectedNetName: pinNetValue,
                });
            }
            const connectionKey = `${connection.moduleRef}:${connection.pinRef}`;
            if (connectionSet.has(connectionKey)) {
                innerResults.push({
                    type: ProjectModelValidationErrorType.DuplicateNetConnection,
                    message: `Net "${netName}" in schematic "${designId}" has duplicate connection to module "${connection.moduleRef}" pin "${connection.pinRef}"`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                    designId,
                    netName,
                    moduleRef: connection.moduleRef,
                    pinRef: connection.pinRef,
                });
            }
            connectionSet.add(connectionKey);
        }
    }
    const allNetConnections = new Set();
    for (const net of Object.values(schematic.nets)) {
        for (const connection of net.connections) {
            allNetConnections.add(`${connection.moduleRef}:${connection.pinRef}`);
        }
    }
    for (const module of schematic.modules) {
        for (const [pinRef, netName] of Object.entries(module.pinNets)) {
            const connectionKey = `${module.ref}:${pinRef}`;
            if (!allNetConnections.has(connectionKey)) {
                innerResults.push({
                    type: ProjectModelValidationErrorType.OrphanedPinConnection,
                    message: `Module "${module.ref}" in schematic "${designId}" has pin "${pinRef}" in pinNets but it doesn't appear in any net connections`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                    designId,
                    moduleRef: module.ref,
                    pinRef,
                    netName,
                });
            }
            if (!schematic.nets[netName] && netName !== undefined) {
                innerResults.push({
                    type: ProjectModelValidationErrorType.InvalidPinNetReference,
                    message: `Module "${module.ref}" in schematic "${designId}" has pin "${pinRef}" referencing non-existent net "${netName}"`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
                    designId,
                    moduleRef: module.ref,
                    pinRef,
                    netName,
                });
            }
        }
    }
    const result = {
        type: 'net-validation',
        message: innerResults.length === 0 ?
            `All nets in schematic "${designId}" are valid`
            : `Net validation found issues in schematic "${designId}"`,
        innerResults,
        designId,
    };
    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(result);
    return result;
}
function validateMultiInstanceConfiguration(schematic, designId) {
    const innerResults = [];
    if (!schematic.multiInstanceConfiguration) {
        return {
            type: 'multi-instance-validation',
            message: 'No multi-instance configuration to validate',
            innerResults: [],
        };
    }
    const config = schematic.multiInstanceConfiguration;
    const instanceNames = new Set();
    const duplicateNames = new Set();
    for (const instance of config.instances) {
        if (instanceNames.has(instance.instanceName)) {
            duplicateNames.add(instance.instanceName);
        }
        instanceNames.add(instance.instanceName);
    }
    if (duplicateNames.size > 0) {
        innerResults.push({
            type: ProjectModelValidationErrorType.DuplicateInstanceName,
            message: `Duplicate instance names in multi-instance schematic "${designId}": ${Array.from(duplicateNames).join(', ')}`,
            severity: sigma_engine_shared_1.BoundaryLevel.Error,
            designId,
            duplicateNames: Array.from(duplicateNames),
        });
    }
    for (const instance of config.instances) {
        if (instance.instanceSheetIndex < 0 || !Number.isInteger(instance.instanceSheetIndex)) {
            innerResults.push({
                type: ProjectModelValidationErrorType.InvalidInstanceSheetIndex,
                message: `Instance "${instance.instanceName}" in schematic "${designId}" has invalid sheet index: ${instance.instanceSheetIndex}`,
                severity: sigma_engine_shared_1.BoundaryLevel.Error,
                designId,
                instanceName: instance.instanceName,
                instanceSheetIndex: instance.instanceSheetIndex,
            });
        }
    }
    const result = {
        type: 'multi-instance-validation',
        message: innerResults.length === 0 ?
            `Multi-instance configuration in schematic "${designId}" is valid`
            : `Multi-instance validation found issues in schematic "${designId}"`,
        innerResults,
        designId,
    };
    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(result);
    return result;
}
function getNumericPinRange(designator) {
    const range = /^(\d+)-(\d+)$/.exec(designator);
    if (!range) {
        return undefined;
    }
    const firstPin = Number(range[1]);
    const lastPin = Number(range[2]);
    return lastPin > firstPin ? [firstPin, lastPin] : undefined;
}
function countPhysicalPinsForDesignator(designator) {
    const range = getNumericPinRange(designator);
    return range ? range[1] - range[0] + 1 : 1;
}
function sourceSymbolDeclaresGroupedSignalSharing(component, pinRef, signalNames) {
    if (!getNumericPinRange(pinRef) || new Set(signalNames).size !== signalNames.length) {
        return false;
    }
    return component.symbols.some((symbol) => {
        const declaredPinNames = new Set(symbol.pins.filter((pin) => pin.ref === pinRef).map((pin) => pin.name));
        return signalNames.every((signalName) => declaredPinNames.has(signalName));
    });
}
function validateComponents(projectModel, opts) {
    const innerResults = [];
    const padReferenceSeverity = projectModel.type === 'kicad' ? sigma_engine_shared_1.BoundaryLevel.Warning : sigma_engine_shared_1.BoundaryLevel.Error;
    for (const [componentId, component] of Object.entries(projectModel.components)) {
        if (opts.missingFootprintsSeverity && component.footprints.length === 0 && !component.isVirtual) {
            innerResults.push({
                type: ProjectModelValidationErrorType.MissingFootprints,
                message: `Component "${componentId}" has no footprints`,
                severity: opts.missingFootprintsSeverity,
                componentId,
            });
        }
        if (opts.missingSymbolsSeverity && component.symbols.length === 0) {
            innerResults.push({
                type: ProjectModelValidationErrorType.MissingSymbols,
                message: `Component "${componentId}" has no symbols`,
                severity: opts.missingSymbolsSeverity,
                componentId,
            });
        }
        if (typeof component.numPins === 'number' && component.numPins > 0) {
            const physicalPinDesignators = new Set(component.symbols.flatMap((symbol) => symbol.pins).map((pin) => pin.ref));
            const nonMechanicalPinDesignators = [...physicalPinDesignators].filter((designator) => !/^MT\d+$/.test(designator));
            const actualPinCount = [...physicalPinDesignators].reduce((count, designator) => count + countPhysicalPinsForDesignator(designator), 0);
            const actualPinCountExcludingMechanical = nonMechanicalPinDesignators.reduce((count, designator) => count + countPhysicalPinsForDesignator(designator), 0);
            if (component.numPins !== actualPinCount && component.numPins !== actualPinCountExcludingMechanical) {
                innerResults.push({
                    type: ProjectModelValidationErrorType.PinCountMismatch,
                    message: `Component "${componentId}" declares ${component.numPins} pins but its unique symbol designators represent ${actualPinCount} physical pins (${actualPinCountExcludingMechanical} excluding MT mounting pins)`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                    componentId,
                    declaredCount: component.numPins,
                    actualCount: actualPinCount,
                    actualCountExcludingMechanical: actualPinCountExcludingMechanical,
                });
            }
        }
        if (typeof component.numPads === 'number') {
            const actualPadCount = component.footprints.reduce((sum, footprint) => sum + footprint.pads.length, 0);
            if (actualPadCount !== component.numPads) {
                innerResults.push({
                    type: ProjectModelValidationErrorType.PadCountMismatch,
                    message: `Component "${componentId}" declares ${component.numPads} pads but has ${actualPadCount} pads in footprints`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
                    componentId,
                    declaredCount: component.numPads,
                    actualCount: actualPadCount,
                });
            }
        }
        const allPinRefs = new Set();
        for (const symbol of component.symbols) {
            for (const pin of symbol.pins) {
                allPinRefs.add(pin.ref);
            }
        }
        const allPadRefs = new Set();
        for (const footprint of component.footprints) {
            for (const pad of footprint.pads) {
                allPadRefs.add(pad.ref);
            }
        }
        for (const signal of component.signals) {
            for (const pinRef of signal.pinRefs) {
                if (pinRef !== null && !allPinRefs.has(pinRef)) {
                    innerResults.push({
                        type: ProjectModelValidationErrorType.InvalidSignalPinReference,
                        message: `Signal "${signal.name}" in component "${componentId}" references non-existent pin "${pinRef}"`,
                        severity: sigma_engine_shared_1.BoundaryLevel.Error,
                        componentId,
                        signalName: signal.name,
                        pinRef,
                    });
                }
            }
            for (const padRef of signal.padRefs) {
                if (!allPadRefs.has(padRef)) {
                    innerResults.push({
                        type: ProjectModelValidationErrorType.InvalidSignalPadReference,
                        message: `Signal "${signal.name}" in component "${componentId}" references non-existent pad "${padRef}"`,
                        severity: padReferenceSeverity,
                        componentId,
                        signalName: signal.name,
                        padRef,
                    });
                }
            }
        }
        const pinToSignals = new Map();
        for (const signal of component.signals) {
            for (const pinRef of signal.pinRefs) {
                if (pinRef !== null) {
                    const signals = pinToSignals.get(pinRef) ?? [];
                    signals.push(signal.name);
                    pinToSignals.set(pinRef, signals);
                }
            }
        }
        for (const [pinRef, signalNames] of pinToSignals) {
            if (signalNames.length > 1 && !sourceSymbolDeclaresGroupedSignalSharing(component, pinRef, signalNames)) {
                innerResults.push({
                    type: ProjectModelValidationErrorType.DuplicatePinInSignals,
                    message: `Pin "${pinRef}" in component "${componentId}" is referenced by multiple signals: ${signalNames.map((s) => `"${s}"`).join(', ')}`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
                    componentId,
                    pinRef,
                    signalNames,
                });
            }
        }
    }
    const result = {
        type: 'component-validation',
        message: innerResults.length === 0 ? 'All components are valid' : 'Component validation found issues',
        innerResults,
    };
    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(result);
    return result;
}
function validateStructuralIssues(projectModel, opts) {
    const innerResults = [];
    const usedComponents = new Set();
    const usedFootprints = new Map();
    for (const module of projectModel.modules) {
        usedComponents.add(module.componentRef);
    }
    for (const design of Object.values(projectModel.designs)) {
        for (const module of design.modules) {
            usedComponents.add(module.componentRef);
            if (design.modelType === 'pcb') {
                if (!usedFootprints.has(module.componentRef)) {
                    usedFootprints.set(module.componentRef, new Set());
                }
                usedFootprints.get(module.componentRef).add(module.footprintRef);
            }
        }
    }
    if (opts.unusedComponentSeverity) {
        for (const componentId of Object.keys(projectModel.components)) {
            if (!usedComponents.has(componentId)) {
                innerResults.push({
                    type: ProjectModelValidationErrorType.UnusedComponent,
                    message: `Component "${componentId}" is not used by any module`,
                    severity: opts.unusedComponentSeverity,
                    componentId,
                });
            }
        }
    }
    if (opts.unusedFootprintSeverity) {
        for (const [componentId, component] of Object.entries(projectModel.components)) {
            const usedFootprintRefs = usedFootprints.get(componentId) ?? new Set();
            for (const footprint of component.footprints) {
                if (!usedFootprintRefs.has(footprint.ref)) {
                    innerResults.push({
                        type: ProjectModelValidationErrorType.UnusedFootprint,
                        message: `Footprint "${footprint.ref}" in component "${componentId}" is not used by any module`,
                        severity: opts.unusedFootprintSeverity,
                        componentId,
                        footprintRef: footprint.ref,
                    });
                }
            }
        }
    }
    const result = {
        type: 'structural-validation',
        message: innerResults.length === 0 ? 'No structural issues found' : 'Structural validation found issues',
        innerResults,
    };
    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(result);
    return result;
}
//# sourceMappingURL=validateProjectModel.js.map